// @mui
import Button from '@mui/material/Button'
import TableRow from '@mui/material/TableRow'
import Checkbox from '@mui/material/Checkbox'
import TableCell from '@mui/material/TableCell'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
// hooks
import { useBoolean } from 'src/hooks/use-boolean'
// types
// components
import Label from 'src/components/label'
import Iconify from 'src/components/iconify'
import { ConfirmDialog } from 'src/components/custom-dialog'
import { format } from 'date-fns'

import { IEventItem } from 'src/types/events'
import { paths } from 'src/routes/paths'
import { useRouter } from 'src/routes/hook'
//

// ----------------------------------------------------------------------

export const roleName = {
  admin: 'Administrador',
  user: 'Usuário',
  franchisee: 'Franqueado',
}

type Props = {
  selected: boolean
  onEditRow: VoidFunction
  row: IEventItem
  refetch: VoidFunction
  onSelectRow: VoidFunction
  onDeleteRow: VoidFunction
}

export default function EventTableRow({
  row,
  selected,
  refetch,
  onEditRow,
  onSelectRow,
  onDeleteRow,
}: Props) {
  const { name, date, active, local, managers, installmentConfig, _id } = row
  const router = useRouter()
  const confirm = useBoolean()

  const handleMenuClick = () => {
    router.push(paths.dashboard.events.menu(_id))
  }

  return (
    <>
      <TableRow hover selected={selected}>
        <TableCell padding='checkbox'>
          <Checkbox checked={selected} onClick={onSelectRow} />
        </TableCell>

        {/* Nome */}
        <TableCell>
          <Stack spacing={0.5}>
            <Typography variant='subtitle2' noWrap sx={{ maxWidth: 180 }}>
              {name}
            </Typography>
            {installmentConfig?.enabled && (
              <Chip
                size='small'
                label={`Parcela até ${installmentConfig.maxInstallments}x`}
                color='info'
                variant='soft'
                sx={{ width: 'fit-content', height: 18, fontSize: '0.65rem' }}
              />
            )}
          </Stack>
        </TableCell>

        {/* Local */}
        <TableCell>
          <Stack direction='row' alignItems='center' spacing={0.5}>
            <Iconify icon='solar:map-point-bold' width={16} sx={{ color: 'text.secondary' }} />
            <Typography variant='body2' noWrap sx={{ maxWidth: 120 }}>
              {local || 'Não informado'}
            </Typography>
          </Stack>
        </TableCell>

        {/* Data do Evento */}
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Stack direction='row' alignItems='center' spacing={0.5}>
            <Iconify icon='solar:calendar-bold' width={16} sx={{ color: 'text.secondary' }} />
            <Typography variant='body2'>
              {date ? format(new Date(date), 'dd/MM/yyyy') : '-'}
            </Typography>
          </Stack>
        </TableCell>

        {/* Vendedores */}
        <TableCell>
          <Stack direction='row' alignItems='center' spacing={0.5}>
            <Iconify
              icon='solar:users-group-rounded-bold'
              width={18}
              sx={{ color: 'primary.main' }}
            />
            <Typography variant='body2' fontWeight={600} color='primary.main'>
              {managers?.length || 0}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {managers?.length === 1 ? 'vendedor' : 'vendedores'}
            </Typography>
          </Stack>
        </TableCell>

        {/* Status */}
        <TableCell>
          <Label
            variant='soft'
            color={(active && 'success') || (!active && 'warning') || 'default'}
          >
            {active ? 'Ativo' : 'Inativo'}
          </Label>
        </TableCell>

        {/* Ações */}
        <TableCell align='right' sx={{ px: 1, whiteSpace: 'nowrap' }}>
          <Stack direction='row' spacing={0.5} alignItems='center' justifyContent='flex-end'>
            <Button
              variant='contained'
              size='small'
              onClick={handleMenuClick}
              startIcon={<Iconify icon='solar:document-text-bold' width={18} />}
              sx={{
                minWidth: 100,
                bgcolor: 'primary.main',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.75rem',
                px: 1.5,
                py: 0.5,
                borderRadius: 1.5,
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
            >
              Cardápio
            </Button>
            <Tooltip title='Editar' placement='top' arrow>
              <IconButton size='small' onClick={() => onEditRow()}>
                <Iconify icon='solar:pen-bold' width={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title='Deletar' placement='top' arrow>
              <IconButton
                size='small'
                onClick={() => {
                  confirm.onTrue()
                }}
              >
                <Iconify color='error.main' icon='solar:trash-bin-trash-bold' width={20} />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title='Delete'
        content='Tem certeza que quer deletar?'
        action={
          <Button variant='contained' color='error' onClick={onDeleteRow}>
            Deletar
          </Button>
        }
      />
    </>
  )
}
